import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { SplitPane } from 'react-collapse-pane';
import PropTypes from 'prop-types';

import CodePanel from './components/CodePanel/index';
import NavigationBar from './components/NavigationBar/index';
import OperationsMenu from './components/OperationsMenu/index';
import PreviewPanel from './components/PreviewPanel/index';
import EventPanel from './components/EventPanel/index';
import ExampleGallery from './components/ExampleGallery/index';
import './styles.scss';
import { loadSession } from './store/CodePanel/actions';
import { selectDarkToggle } from './store/NavigationBar/selectors';
import {
  COLLAPSED_SIZE,
  COLLAPSE_TRANSITION_TIMEOUT,
  COLLAPSE_DIRECTION_RIGHT,
  SPLIT_DIRECTION_HORIZONTAL,
  SPLIT_DIRECTION_VERTICAL,
  OPERATION_MENU_MIN_SIZE,
  OPERATION_MENU_MAX_SIZE,
  INITIAL_SIZE_RATIO_BETWEEN_OPERATION_MENU_AND_NON_OPERATION_MENU,
  INITIAL_SIZE_RATIO_BETWEEN_CODE_PANEL_AND_EVENT_PANEL,
  INITIAL_SIZE_RATIO_BETWEEN_CODE_PANEL_AND_PREVIEW_PANEL,
  HORIZONTAL_RESIZER_OPTIONS,
  VERTICAL_RESIZER_OPTIONS,
  OPERATION_MENU_VERTICAL_RESIZER_OPTIONS,
  PREVIEW_COLLAPSE_PANE_SELECTOR,
  EVENT_COLLAPSE_PANE_SELECTOR,
  DRAGGING_TRANSITION_STYLE,
  COLLAPSE_TRANSITION_STYLE,
  RESIZER_BETWEEN_PREVIEW_AND_CODE_SELECTOR,
  RESIZER_BETWEEN_PREVIEW_AND_CODE_DEFAULT_HEIGHT,
  RESIZER_BETWEEN_PREVIEW_AND_CODE_DRAG_HEIGHT,
} from './constants/resizing-components';
import { CARD_HEADER_HEIGHT, SCROLLBAR_HEIGHT } from './constants';
import { loadTheme } from './store/NavigationBar/actions';
import { sendGAEvent } from './store/GoogleAnalytics/actions';
import {
  CLICK_FULL_SCREEN_BUTTON,
  COLLAPSE_EXPAND_EVENT_PANEL_LABEL,
  COLLAPSE_EXPAND_OPERATIONS_MENU_LABEL,
  COLLAPSE_PANEL,
  EXPAND_PANEL,
  RESIZE_CODE_PANEL_AND_EVENT_PANEL_LABEL,
  RESIZE_PANEL,
  RESIZE_PREVIEW_PANEL_LABEL,
} from './constants/google-analytics';
import EnvironmentSetting from './components/EnvironmentSetting';

const App = ({ isDarkTheme, onLoadSession, onLoadTheme, onSendGAEvent }) => {
  const [previewCollapsePane, setPreviewCollapsePane] = useState(null);
  const [eventCollapsePane, setEventCollapsePane] = useState(null);

  const [isEventPanelCollapsed, setIsEventPanelCollapsed] = useState(false);
  const [isOperationsMenuCollapsed, setIsOperationsMenuCollapsed] = useState(false);

  // This effect handles loading the session code and theme
  useEffect(() => {
    onLoadSession();
    onLoadTheme();
  }, [onLoadSession, onLoadTheme]);

  // This function handles opening the preview panel in full screen
  function openFullscreen(e) {
    // We stop propagation to override the parent onclick behavior
    e.stopPropagation();
    onSendGAEvent(CLICK_FULL_SCREEN_BUTTON);
    const elem = document.getElementsByClassName('preview-panel')[0];
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      /* Safari */
      elem.webkitRequestFullscreen();
    }
  }

  // Add the fullscreen button as an html element to the resizer
  const addFullScreenButton = () => {
    const resizerBetweenPreviewAndCodeSelector = document.querySelector(
      RESIZER_BETWEEN_PREVIEW_AND_CODE_SELECTOR,
    );
    if (resizerBetweenPreviewAndCodeSelector) {
      const fullScreenButtonDiv = document.createElement('div');
      fullScreenButtonDiv.className = 'full-screen-button-container';
      fullScreenButtonDiv.onmousedown = (e) => {
        // Change this current div to drag height
        e.target.style.height = RESIZER_BETWEEN_PREVIEW_AND_CODE_DRAG_HEIGHT;
      };
      fullScreenButtonDiv.onmouseup = (e) => {
        // Change this current div to default height
        e.target.style.height = RESIZER_BETWEEN_PREVIEW_AND_CODE_DEFAULT_HEIGHT;
      };
      fullScreenButtonDiv.onmouseleave = (e) => {
        // Change this current div to default height
        if (e.target.style.height !== RESIZER_BETWEEN_PREVIEW_AND_CODE_DEFAULT_HEIGHT) {
          e.target.style.height = RESIZER_BETWEEN_PREVIEW_AND_CODE_DEFAULT_HEIGHT;
        }
      };
      const fullScreenButton = document.createElement('span');
      fullScreenButton.onclick = openFullscreen;
      // We stop propagation to override the parent mouse down behavior
      fullScreenButton.onmousedown = (e) => e.stopPropagation();
      // We stop propagation to override the parent mouse up behavior
      fullScreenButton.onmouseup = (e) => e.stopPropagation();
      // We stop propagation to override the parent mouse leave behavior
      fullScreenButton.onmouseleave = (e) => e.stopPropagation();
      fullScreenButton.innerHTML = 'fullscreen';
      fullScreenButton.className = 'full-screen-button material-icons';
      fullScreenButtonDiv.appendChild(fullScreenButton);
      resizerBetweenPreviewAndCodeSelector.appendChild(fullScreenButtonDiv);
    }
  };

  // Resizes the code panel to appriorate size with the resizer
  const resizeCodePanel = () => {
    const codePanelElement = document.getElementsByClassName('cm-scroller')[0];
    const codePanelContainerElement = document.getElementsByClassName('code-panel')[0];
    if (!!codePanelElement && !!codePanelContainerElement) {
      // Min height of 100px to prevent viewport bug (a height that is too low or 0 will cause the bug).
      codePanelElement.setAttribute(
        'style',
        `height: ${Math.max(
          codePanelContainerElement.clientHeight - CARD_HEADER_HEIGHT - SCROLLBAR_HEIGHT,
          100,
        )}px;`,
      );
    }
  };

  // resize code panel during when it is initialized and add the fullscreen button
  useEffect(() => {
    resizeCodePanel();
    addFullScreenButton();
    setPreviewCollapsePane(document.querySelector(PREVIEW_COLLAPSE_PANE_SELECTOR));
    setEventCollapsePane(document.querySelector(EVENT_COLLAPSE_PANE_SELECTOR));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `addFullScreenButton` should not be passed into the dependencies array
  }, []);

  // return the index of 'transition' in the style string.
  const getTransitionIndex = (styleString) => {
    if (typeof styleString === 'string') {
      return styleString.indexOf('transition') === -1
        ? styleString.length
        : styleString.indexOf('transition');
    }
    return 0;
  };

  return (
    <div className={isDarkTheme ? 'app dark' : 'app'}>
      <EnvironmentSetting />
      <ExampleGallery />
      <NavigationBar />
      <div className={isDarkTheme ? 'content dark' : 'content'}>
        <SplitPane
          className="top-level-collapse-pane"
          split={SPLIT_DIRECTION_VERTICAL}
          resizerOptions={OPERATION_MENU_VERTICAL_RESIZER_OPTIONS}
          collapse={{
            collapsedSize: COLLAPSED_SIZE,
            collapseTransitionTimeout: COLLAPSE_TRANSITION_TIMEOUT,
          }}
          // initial size ratio between <OperationMenu /> and div.non-operation-menu
          initialSizes={INITIAL_SIZE_RATIO_BETWEEN_OPERATION_MENU_AND_NON_OPERATION_MENU}
          minSizes={[OPERATION_MENU_MIN_SIZE]}
          maxSizes={[OPERATION_MENU_MAX_SIZE]}
          flexGrows={[0, 1]}
          flexShrinks={[0, 1]}
          hooks={{
            onSaveSizes: (sizes) => {
              if (!isOperationsMenuCollapsed && sizes[0] === COLLAPSED_SIZE) {
                onSendGAEvent(COLLAPSE_PANEL, COLLAPSE_EXPAND_OPERATIONS_MENU_LABEL);
                setIsOperationsMenuCollapsed(true);
              } else if (isOperationsMenuCollapsed) {
                onSendGAEvent(EXPAND_PANEL, COLLAPSE_EXPAND_OPERATIONS_MENU_LABEL);
                setIsOperationsMenuCollapsed(false);
              }
            },
          }}
        >
          <OperationsMenu />

          <div className="non-operations-menu">
            <SplitPane
              split={SPLIT_DIRECTION_HORIZONTAL}
              resizerOptions={HORIZONTAL_RESIZER_OPTIONS}
              collapse={false}
              className="non-operations-menu-collapse-pane"
              hooks={{
                onChange: () => {
                  resizeCodePanel();
                },
                onCollapse: (collapsedSizes) => {
                  setTimeout(
                    () => {
                      resizeCodePanel();
                    },
                    // add 50ms to ensure the height could be set to the height after the transition
                    COLLAPSE_TRANSITION_TIMEOUT + 50,
                  );
                  // when both elements in `collapsedSizes` are null, the panes didn't collapse.
                  // We only want to run this code snippet when the pane is collapsed.
                  if (
                    previewCollapsePane &&
                    (collapsedSizes[0] !== null || collapsedSizes[1] !== null)
                  ) {
                    // add `transition: flex-basis` to the preview panel, which is needed for the expansion transition
                    const previewCollapsePaneStyle = previewCollapsePane.getAttribute('style');
                    const endIndex = getTransitionIndex(previewCollapsePaneStyle);
                    previewCollapsePane.setAttribute(
                      'style',
                      previewCollapsePaneStyle.substr(0, endIndex) + COLLAPSE_TRANSITION_STYLE,
                    );
                  }
                },
                onDragStarted: () => {
                  // remove `transition: flex-basis` from the preview panel. Otherwise, dragging will feel laggy.
                  if (previewCollapsePane) {
                    const previewCollapsePaneStyle = previewCollapsePane.getAttribute('style');
                    const endIndex = getTransitionIndex(previewCollapsePaneStyle);
                    previewCollapsePane.setAttribute(
                      'style',
                      previewCollapsePaneStyle.substr(0, endIndex) + DRAGGING_TRANSITION_STYLE,
                    );

                    onSendGAEvent(RESIZE_PANEL, RESIZE_PREVIEW_PANEL_LABEL);
                  }
                },
              }}
              // initial size ratio between div.code-panel-and-events-panel and div.preview-panel
              initialSizes={INITIAL_SIZE_RATIO_BETWEEN_CODE_PANEL_AND_PREVIEW_PANEL}
            >
              <div className="code-panel-and-events-panel">
                <SplitPane
                  className="event-collapse-pane"
                  split={SPLIT_DIRECTION_VERTICAL}
                  resizerOptions={VERTICAL_RESIZER_OPTIONS}
                  collapse={{
                    collapsedSize: COLLAPSED_SIZE,
                    collapseTransitionTimeout: COLLAPSE_TRANSITION_TIMEOUT,
                    collapseDirection: COLLAPSE_DIRECTION_RIGHT,
                  }}
                  // initial size ratio between <CodePanel /> and <EventPanel />
                  initialSizes={INITIAL_SIZE_RATIO_BETWEEN_CODE_PANEL_AND_EVENT_PANEL}
                  minSizes={[500, 260]}
                  flexGrows={[1, 0]}
                  flexShrinks={[1, 0]}
                  hooks={{
                    onCollapse: (collapsedSizes) => {
                      // when both elements in `collapsedSizes` are null, the panes didn't collapse.
                      // We only want to run this code snippet when the pane is collapsed.
                      if (
                        eventCollapsePane &&
                        (collapsedSizes[0] !== null || collapsedSizes[1] !== null)
                      ) {
                        // add `transition: flex-basis` to the event panel, which is needed for the expansion transition
                        const eventCollapsePaneStyle = eventCollapsePane.getAttribute('style');
                        const endIndex = getTransitionIndex(eventCollapsePaneStyle);
                        eventCollapsePane.setAttribute(
                          'style',
                          eventCollapsePaneStyle.substr(0, endIndex) + COLLAPSE_TRANSITION_STYLE,
                        );
                      }
                    },
                    onDragStarted: () => {
                      // remove `transition: flex-basis` from the event panel. Otherwise, dragging will feel laggy.
                      if (eventCollapsePane) {
                        const eventCollapsePaneStyle = eventCollapsePane.getAttribute('style');
                        const endIndex = getTransitionIndex(eventCollapsePaneStyle);
                        eventCollapsePane.setAttribute(
                          'style',
                          eventCollapsePaneStyle.substr(0, endIndex) + DRAGGING_TRANSITION_STYLE,
                        );
                        onSendGAEvent(RESIZE_PANEL, RESIZE_CODE_PANEL_AND_EVENT_PANEL_LABEL);
                      }
                    },
                    onSaveSizes: (sizes) => {
                      if (!isEventPanelCollapsed && sizes[1] === COLLAPSED_SIZE) {
                        onSendGAEvent(COLLAPSE_PANEL, COLLAPSE_EXPAND_EVENT_PANEL_LABEL);
                        setIsEventPanelCollapsed(true);
                      } else if (isEventPanelCollapsed) {
                        onSendGAEvent(EXPAND_PANEL, COLLAPSE_EXPAND_EVENT_PANEL_LABEL);
                        setIsEventPanelCollapsed(false);
                      }
                    },
                  }}
                >
                  <div className="code-panel">
                    <CodePanel />
                  </div>
                  <div className="events-panel">
                    <EventPanel />
                  </div>
                </SplitPane>
              </div>
              <PreviewPanel />
            </SplitPane>
          </div>
        </SplitPane>
      </div>
    </div>
  );
};

App.propTypes = {
  isDarkTheme: PropTypes.bool.isRequired,
  onLoadSession: PropTypes.func.isRequired,
  onLoadTheme: PropTypes.func.isRequired,
  onSendGAEvent: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    isDarkTheme: selectDarkToggle(state),
  };
}
function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      onLoadSession: loadSession,
      onLoadTheme: loadTheme,
      onSendGAEvent: sendGAEvent,
    },
    dispatch,
  );
}
export default connect(mapStateToProps, mapDispatchToProps)(App);
